import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CreatePostForm from '../components/CreatePostForm/CreatePostForm';
import apiClient from '../services/api';
import useResource from '../services/useResource';
import { MaybePaginated, Platform, Post, SocialAccount } from '../types/api';
import './CalendarPage.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/* One-letter glyph + per-platform color, drawn in a small circle. Avoids
   pulling in an icon library for what amounts to four dots. */
const PLATFORM_GLYPH: Record<string, string> = {
  bluesky: 'B',
  instagram: 'I',
  tiktok: 'T',
  facebook: 'F',
};

function PlatformDot({ platform }: { platform: string }): React.ReactElement {
  const glyph = PLATFORM_GLYPH[platform] ?? platform.slice(0, 1).toUpperCase();
  return (
    <span
      className={`platform-dot platform-dot--${platform}`}
      aria-label={platform}
      title={platform}
    >
      {glyph}
    </span>
  );
}

function isoDay(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function buildMonthGrid(monthStart: Date): Date[] {
  const start = new Date(monthStart);
  start.setDate(1 - monthStart.getDay());
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

function postTimestamp(p: Post): Date | null {
  if (p.posted_at) return new Date(p.posted_at);
  if (p.scheduled_at) return new Date(p.scheduled_at);
  return null;
}

interface TooltipState {
  post: Post;
  /* Viewport-coords anchor rect of the chip the tooltip describes. */
  anchor: { top: number; bottom: number; left: number; right: number };
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  /* Lifted-out tooltip state. Rendered with position: fixed at the page
     level so it escapes the calendar grid's overflow:hidden clipping. */
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  /* Small grace period before hiding so the user can move from chip to
     tooltip (e.g. to select text) without it vanishing mid-motion. */
  const hideTimer = useRef<number | null>(null);

  /* Date the user is composing a post for, via inline-create. null = closed.
     Stored as a Date object so we can pass it straight to CreatePostForm
     as initialScheduleAt. */
  const [composeDate, setComposeDate] = useState<Date | null>(null);

  const { data: posts, loading, error, reload } = useResource<Post[], MaybePaginated<Post>>(
    () => apiClient.get<MaybePaginated<Post>>('/posts/posts/'),
    [],
    { initial: [], transform: (d) => (Array.isArray(d) ? d : d.results || []) },
  );

  const { data: accounts } = useResource<SocialAccount[], MaybePaginated<SocialAccount>>(
    () => apiClient.get<MaybePaginated<SocialAccount>>('/accounts/social-accounts/'),
    [],
    { initial: [], transform: (d) => (Array.isArray(d) ? d : d.results || []) },
  );

  const accountPlatform = useMemo(() => {
    const m = new Map<number, Platform | string>();
    for (const a of accounts) m.set(a.id, a.platform);
    return m;
  }, [accounts]);

  function platformsFor(post: Post): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const id of post.social_accounts) {
      const p = accountPlatform.get(id);
      if (p && !seen.has(p)) { seen.add(p); out.push(p); }
    }
    return out;
  }

  const postsByDay = useMemo(() => {
    const map = new Map<string, Post[]>();
    for (const p of posts) {
      const ts = postTimestamp(p);
      if (!ts) continue;
      const key = isoDay(ts);
      const bucket = map.get(key) || [];
      bucket.push(p);
      map.set(key, bucket);
    }
    for (const list of map.values()) {
      list.sort((a, b) => {
        const ta = postTimestamp(a)!.getTime();
        const tb = postTimestamp(b)!.getTime();
        return ta - tb;
      });
    }
    return map;
  }, [posts]);

  const upcoming = useMemo(() => {
    const now = Date.now();
    return posts
      .filter((p) => p.status === 'scheduled' && p.scheduled_at &&
                     new Date(p.scheduled_at).getTime() >= now)
      .sort((a, b) =>
        new Date(a.scheduled_at!).getTime() - new Date(b.scheduled_at!).getTime())
      .slice(0, 10);
  }, [posts]);

  const cells = useMemo(() => buildMonthGrid(cursor), [cursor]);
  const todayKey = isoDay(new Date());
  const monthLabel = cursor.toLocaleDateString(undefined,
    { month: 'long', year: 'numeric' });

  function goto(deltaMonths: number): void {
    setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + deltaMonths, 1));
  }

  function openPost(post: Post): void {
    navigate(`/create?postId=${post.id}`);
  }

  function openCompose(day: Date): void {
    /* Default the schedule field to noon of the picked day so the user
       has a sensible time pre-filled but can change it. */
    const d = new Date(day);
    d.setHours(12, 0, 0, 0);
    setComposeDate(d);
  }

  function closeCompose(reloadPosts: boolean): void {
    setComposeDate(null);
    if (reloadPosts) reload();
  }

  function timeOf(p: Post): string {
    const ts = postTimestamp(p);
    if (!ts) return '';
    return ts.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }

  function showTooltip(post: Post, el: HTMLElement): void {
    if (hideTimer.current) { window.clearTimeout(hideTimer.current); hideTimer.current = null; }
    const r = el.getBoundingClientRect();
    setTooltip({
      post,
      anchor: { top: r.top, bottom: r.bottom, left: r.left, right: r.right },
    });
  }

  function scheduleHide(): void {
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setTooltip(null), 120);
  }

  function cancelHide(): void {
    if (hideTimer.current) { window.clearTimeout(hideTimer.current); hideTimer.current = null; }
  }

  /* How many chips fit before we collapse to "+N more". 2 keeps every cell
     visually balanced and leaves room for the day number. */
  const MAX_CHIPS_PER_DAY = 2;

  /* Tooltip positioning: float above the chip by default, flip to below
     when the chip is in the upper half of the viewport so the popover
     stays on-screen. Horizontal: pin to chip's left, clamp to viewport. */
  const tooltipStyle: React.CSSProperties | undefined = (() => {
    if (!tooltip) return undefined;
    const TOOLTIP_W = 240;
    const PAD = 8;
    const vw = typeof window === 'undefined' ? 1280 : window.innerWidth;
    const vh = typeof window === 'undefined' ? 800 : window.innerHeight;
    const flipBelow = tooltip.anchor.top < vh * 0.4;
    let left = tooltip.anchor.left;
    if (left + TOOLTIP_W + PAD > vw) left = vw - TOOLTIP_W - PAD;
    if (left < PAD) left = PAD;
    return flipBelow
      ? { top: tooltip.anchor.bottom + 6, left }
      : { top: undefined, bottom: vh - tooltip.anchor.top + 6, left };
  })();

  const tooltipPlats = tooltip ? platformsFor(tooltip.post) : [];
  const tooltipDesc = tooltip ? (tooltip.post.description?.trim() || '(no description)') : '';

  return (
    <div className="calendar-page">
      <header className="calendar-header">
        <h2>{monthLabel}</h2>
        <div className="calendar-nav">
          <button className="ghost btn--sm" onClick={() => goto(-1)} aria-label="Previous month">‹</button>
          <button className="ghost btn--sm" onClick={() => {
            const now = new Date();
            setCursor(new Date(now.getFullYear(), now.getMonth(), 1));
          }}>Today</button>
          <button className="ghost btn--sm" onClick={() => goto(1)} aria-label="Next month">›</button>
        </div>
      </header>

      {error && <div className="posts-error">{error.message}</div>}
      {loading && <p className="muted">Loading…</p>}

      <div className="calendar-grid">
        {WEEKDAYS.map((w) => (
          <div key={w} className="calendar-weekday">{w}</div>
        ))}
        {cells.map((day) => {
          const key = isoDay(day);
          const inMonth = day.getMonth() === cursor.getMonth();
          const isToday = key === todayKey;
          const dayPosts = postsByDay.get(key) || [];
          const visible = dayPosts.slice(0, MAX_CHIPS_PER_DAY);
          const overflow = dayPosts.length - visible.length;
          return (
            <div
              key={key}
              className={[
                'calendar-cell',
                inMonth ? '' : 'is-other-month',
                isToday ? 'is-today' : '',
              ].filter(Boolean).join(' ')}
            >
              <div className="calendar-cell-head">
                <button
                  type="button"
                  className="calendar-cell-add"
                  onClick={() => openCompose(day)}
                  aria-label={`New post on ${day.toLocaleDateString()}`}
                  title="New post on this day"
                >
                  +
                </button>
                <span className="calendar-cell-day">{day.getDate()}</span>
              </div>
              <ul className="calendar-cell-posts">
                {visible.map((p) => {
                  const plats = platformsFor(p);
                  const desc = p.description?.trim() || '(no description)';
                  return (
                    <li key={p.id}>
                      <button
                        className={`calendar-post-chip status-${p.status}`}
                        onClick={() => openPost(p)}
                        onMouseEnter={(e) => showTooltip(p, e.currentTarget)}
                        onMouseLeave={scheduleHide}
                        onFocus={(e) => showTooltip(p, e.currentTarget)}
                        onBlur={scheduleHide}
                        aria-label={`${timeOf(p)} — ${desc}`}
                      >
                        <span className="calendar-post-time">{timeOf(p)}</span>
                        <span className="calendar-post-dots">
                          {plats.length === 0 ? (
                            <span className="platform-dot platform-dot--none" title="No account">·</span>
                          ) : plats.map((pl) => (
                            <PlatformDot key={pl} platform={pl} />
                          ))}
                        </span>
                      </button>
                    </li>
                  );
                })}
                {overflow > 0 && (
                  <li className="calendar-more muted">+{overflow} more</li>
                )}
              </ul>
            </div>
          );
        })}
      </div>

      {tooltip && tooltipStyle && (
        <div
          className="calendar-chip-tooltip"
          role="tooltip"
          style={tooltipStyle}
          onMouseEnter={cancelHide}
          onMouseLeave={scheduleHide}
        >
          <div className="calendar-chip-tooltip-head">
            <span className={`status-badge status-${tooltip.post.status}`}>
              {tooltip.post.status}
            </span>
            <span className="muted">{timeOf(tooltip.post)}</span>
          </div>
          <div className="calendar-chip-tooltip-body">{tooltipDesc}</div>
          {tooltipPlats.length > 0 && (
            <div className="calendar-chip-tooltip-plats">
              {tooltipPlats.map((pl) => <PlatformDot key={pl} platform={pl} />)}
            </div>
          )}
        </div>
      )}

      {composeDate && (
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeCompose(false); }}
        >
          <div className="modal__panel">
            <header className="modal__head">
              <h3>New post — {composeDate.toLocaleDateString(undefined,
                { weekday: 'short', month: 'short', day: 'numeric' })}</h3>
              <button
                type="button"
                className="modal__close"
                onClick={() => closeCompose(false)}
                aria-label="Close"
              >
                ×
              </button>
            </header>
            <div className="modal__body">
              <CreatePostForm
                embedded
                initialScheduleAt={composeDate}
                onSaved={() => closeCompose(true)}
                onCancel={() => closeCompose(false)}
              />
            </div>
          </div>
        </div>
      )}

      <section className="calendar-upcoming">
        <h3>Upcoming</h3>
        {upcoming.length === 0 && (
          <p className="muted">No scheduled posts. Add a date on the Create Post form.</p>
        )}
        <ul className="calendar-upcoming-list">
          {upcoming.map((p) => (
            <li key={p.id} className="calendar-upcoming-row">
              <span className="status-badge status-scheduled">scheduled</span>
              <span className="calendar-upcoming-when">
                {new Date(p.scheduled_at!).toLocaleString()}
              </span>
              <span className="calendar-upcoming-text">
                {p.description || '(no description)'}
              </span>
              <button className="secondary btn--sm" onClick={() => openPost(p)}>
                Edit
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
